// noinspection ExceptionCaughtLocallyJS

import { createAsyncThunk } from '@reduxjs/toolkit';
import { RequestInt } from 'interfaces/request.interface';
import { ICreateResponse } from 'interfaces/response.interface';
import {
  IResponseAmbulance,
  IResponseAmbulanceDetail,
  IResponsePersonnel,
  IResponsePersonnelDetail,
  ResponseFormData,
} from 'pages/EMSResponse/hooks/useResponseForm';
import apiFetchMagic from 'services/apiFetchMagic';
import {
  addResponse,
  changeResponse,
  getRequestMagic,
} from '../../../../services/requestEndPoints';
import { RootState } from 'store';
import { ResponseStatus } from '../../../../common/enums';

export const fetchRequest = createAsyncThunk('response/fetchRequest', async (_, { getState }) => {
  try {
    const state = getState() as RootState;
    const accessToken = state.responsePage.accessToken;
    const endpoint = getRequestMagic(accessToken);
    let response = await apiFetchMagic<RequestInt>(endpoint).then((res) => res?.data || null);

    if (response) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (response.requests) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        response.requests = { ...response.requests,
          agency: response.agency,
          status: response.status,
          primaryContactPerson: response.primaryContactPerson,
          primaryContactPhone: response.primaryContactPhone,
          primaryContactNotes: response.primaryContactNotes,
          declineMessage: response?.response?.declineMessage,
          offeredAt: response?.response?.createdAt,
          acceptedAt: response?.response?.updatedAt,
        };
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        response = response.requests;
      }
      return response;
    } else {
      throw 'Submitted credentials is expired or invalid.';
    }
  } catch (error) {
    let message = 'Something went wrong!';
    if (error instanceof Error) message = error.message;
    else {
      if (typeof error === 'object' && error !== null) {
        if ('message' in error) {
          message = String(error.message);
        }
      }
    }
    console.log('fetchRequest catch:', message);
    throw Error(message);
  }
});

export const createResponse = createAsyncThunk(
  'response/create',
  async (data: ResponseFormData, { getState }) => {
    try {
      const state = getState() as RootState;
      const accessToken = state.responsePage.accessToken;
      const request = state.responsePage.request;

      const payload: ICreateResponse = {
        requestId: request?.id || '',
        agencyId: request?.agency?.id || '',
        ambulance: cleanUpEmptyAmbulance(data),
        personnel: cleanupEmptyPersonnel(data),
        status: ResponseStatus.OPEN,
        declineMessage: '',
        primaryContactPerson: data.primaryContactPerson || '',
        primaryContactPhone: data.primaryContactPhone || '',
        primaryContactNotes: data.primaryContactNotes || '',
      };

      const params = {
        authorize: false,
        accessToken: accessToken,
      };

      const endpoint = addResponse(payload, params);
      const response = await apiFetchMagic<{ data: string }>(endpoint)
        .then((res) => res?.data || null)
        .catch((e) => {
          throw e;
        });

      if (response && response.data === 'Success') {
        return response;
      } else {
        throw 'Something went wrong';
      }
    } catch (error) {
      let message = 'Something went wrong!';
      if (error instanceof Error) message = error.message;
      else {
        if (typeof error === 'object' && error !== null) {
          if ('message' in error) {
            message = String(error.message);
          }
        }
      }
      console.log('create response catch:', message);
      throw Error(message);
    }
  },
);

export const updateResponse = createAsyncThunk(
  'response/update',
  async (data: ResponseFormData, { getState }) => {
    try {
      const state = getState() as RootState;
      const accessToken = state.responsePage.accessToken;
      const request = state.responsePage.request;

      const payload: ICreateResponse = {
        requestId: request?.id || '',
        agencyId: request?.agencyId || '',
        responseId: request?.responseId,
        ambulance: cleanUpEmptyAmbulance(data),
        personnel: cleanupEmptyPersonnel(data),
        status: ResponseStatus.OPEN,
        declineMessage: '',
        primaryContactPerson: data.primaryContactPerson || '',
        primaryContactPhone: data.primaryContactPhone || '',
        primaryContactNotes: data.primaryContactNotes || '',
      };

      const params = {
        authorize: false,
        accessToken: accessToken,
      };

      const endpoint = changeResponse(payload, params);

      const response = await apiFetchMagic<{ data: string }>(endpoint)
        .then((res) => res?.data || null)
        .catch((e) => {
          throw e;
        });

      if (response && response.data) {
        return response;
      } else {
        throw 'Something went wrong';
      }
    } catch (error) {
      let message = 'Something went wrong!';
      if (error instanceof Error) message = error.message;
      else {
        if (typeof error === 'object' && error !== null) {
          if ('message' in error) {
            message = String(error.message);
          }
        }
      }
      console.log('update response catch:', message);
      throw Error(message);
    }
  },
);

function cleanUpEmptyAmbulance(data: ResponseFormData): IResponseAmbulance[] {
  return (
    data.ambulance?.map((a) => {
      const tempA = a;
      const tempItem: IResponseAmbulanceDetail[] = [];
      tempA.items?.forEach((item) => {
        if (data.supportAmbulance && Number(item.number)) {
          tempItem.push(item);
        }
      });
      tempA.items = tempItem;
      return tempA;
    }) || []
  );
}

function cleanupEmptyPersonnel(data: ResponseFormData): IResponsePersonnel[] {
  return (
    data.personnel?.map((p) => {
      const tempP = p;
      const tempItem: IResponsePersonnelDetail[] = [];
      tempP.items?.forEach((item) => {
        if (data.supportPersonnel && Number(item.number)) {
          tempItem.push(item);
        }
      });
      tempP.items = tempItem;
      return tempP;
    }) || []
  );
}
